import React, { useEffect, useMemo, useState } from "react";
import FormModal from "../../../../shared/components/generic/form/form-modal/form-modal";
import TableGeneric from "../../../../shared/components/generic/table/table-generic";
import Pagination from "../../../../shared/components/generic/pagination/pagination";
import PageLoading from "../../../../shared/components/generic/page-loading/page-loading";
import AdminSection from "../../components/admin-section/admin-section";
import { useAdminFormModal } from "../../hooks/useAdminFormModal";
import { showCommonAlert } from "../../../../shared/components/generic/alert/utils/show-common-alert-utils";
import { showConfirmAlert } from "../../../../shared/components/generic/alert/confirm-alert/confirm-alert";
import { showNoRecordsFoundAlert } from "../../utils/admin-utils";
import { removeClearedValidationErrors } from "../../../utils/validation-utils";
import { holidayFormConfig } from "../../config/holiday-management/holiday-form-config";
import { holidayColumnsConfig } from "../../config/holiday-management/holiday-columns-config";
import { useHolidays } from "../../hooks/holiday-management/useHolidays";
import { useHolidayNew } from "../../hooks/holiday-management/useHolidayNew";
import { useHolidayDelete } from "../../hooks/holiday-management/useHolidayDelete";
import { useHolidayById } from "../../hooks/holiday-management/useHolidayById";
import { useHolidayUpdate } from "../../hooks/holiday-management/useHolidayUpdate";
import "./cadastro-feriados.css";

const CadastroFeriados = () => {
    const [holidayPagination, setHolidayPagination] = useState({
        totalElements: 0,
        totalPages: 0,
        page: 0,
        size: 10
    });

    const [holidayFormValues, setHolidayFormValues] = useState({
        name: "",
        month: "",
        day: "",
        year: new Date().getFullYear()
    });

    const [holidayValidationErrors, setHolidayValidationErrors] = useState({});
    const [clearedValidationErrors, setClearedValidationErrors] = useState({});

    const holidayModal = useAdminFormModal();

    const {
        holidays,
        loading: holidaysLoading,
        refetch
    } = useHolidays();

    const {
        loading: createHolidayLoading,
        createHoliday
    } = useHolidayNew();

    const {
        loading: deleteHolidayLoading,
        deleteHoliday
    } = useHolidayDelete();

    const {
        loading: holidayByIdLoading,
        refetch: fetchHolidayById
    } = useHolidayById();

    const {
        loading: updateHolidayLoading,
        updateHoliday
    } = useHolidayUpdate();

    const formFields = useMemo(() => {
        return holidayFormConfig(holidayFormValues);
    }, [holidayFormValues]);

    const visibleHolidayValidationErrors = removeClearedValidationErrors({
        errors: holidayValidationErrors,
        clearedErrors: clearedValidationErrors
    });

    const loadHolidays = async ({
        page = holidayPagination.page,
        size = holidayPagination.size,
        showEmptyAlert = false
    } = {}) => {
        const result = await refetch({ page, size });

        if (!result.ok) {
            await showCommonAlert({
                title: "Erro",
                message: result.error,
                fallbackMessage: "Não foi possível carregar os feriados.",
                icon: "error",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });

            return result;
        }

        setHolidayPagination(result.pagination);

        if (result.empty && showEmptyAlert) {
            await showNoRecordsFoundAlert();
        }

        return result;
    };

    useEffect(() => {
        loadHolidays({
            page: 0,
            size: holidayPagination.size,
            showEmptyAlert: true
        });
    }, []);

    const handleOpenCreate = () => {
        setHolidayValidationErrors({});
        setClearedValidationErrors({});

        setHolidayFormValues({
            name: "",
            month: "",
            day: "",
            year: new Date().getFullYear()
        });

        holidayModal.openCreate();
    };

    const handleCloseHolidayModal = () => {
        setHolidayValidationErrors({});
        setClearedValidationErrors({});
        holidayModal.close();
    };

    const handleHolidayFieldChange = ({ name, values }) => {
        setClearedValidationErrors(previous => ({
            ...previous,
            [name]: true,
            ...(name === "month" ? { day: true } : {})
        }));

        if (name === "month") {
            setHolidayFormValues(previous => ({
                ...previous,
                ...values,
                day: ""
            }));
        }
    };

    const handlePageChange = ({ selected }) => {
        loadHolidays({
            page: selected,
            size: holidayPagination.size
        });
    };

    const handleSubmitHoliday = async values => {
        const payloadValues = {
            ...values,
            year: holidayFormValues.year || new Date().getFullYear()
        };
        const result = holidayModal.isEditing
            ? await updateHoliday(holidayModal.selectedItem.id, payloadValues)
            : await createHoliday(payloadValues);

        if (!result.ok) {
            if (result.reason === "validation") {
                setClearedValidationErrors({});
                setHolidayValidationErrors(result.errors);

                const errorMessages = [
                    ...new Set(Object.values(result.errors || {}))
                ];

                await showCommonAlert({
                    title: "Atenção",
                    html: errorMessages.join("<br />"),
                    icon: "warning",
                    customClass: {
                        container: "common-alert__container",
                        confirmButton: "alert-form-button"
                    }
                });

                return;
            }

            await showCommonAlert({
                title: "Erro",
                message: result.error,
                fallbackMessage: "Não foi possível salvar o feriado.",
                icon: "error",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });

            return;
        }

        setHolidayValidationErrors({});
        setClearedValidationErrors({});
        holidayModal.close();

        await showCommonAlert({
            title: "Sucesso",
            message: holidayModal.isEditing
                ? "Feriado atualizado com sucesso."
                : "Feriado cadastrado com sucesso.",
            icon: "success",
            customClass: {
                container: "common-alert__container",
                confirmButton: "alert-form-button"
            }
        });

        loadHolidays({
            page: holidayModal.isEditing ? holidayPagination.page : 0,
            size: holidayPagination.size,
            showEmptyAlert: true
        });
    };

    const handleDeleteHoliday = async holiday => {
        const result = await showConfirmAlert({
            title: "Excluir feriado",
            description: `Tem certeza que deseja excluir o feriado ${holiday.name}?`,
            confirmLabel: "Excluir",
            cancelLabel: "Cancelar"
        });

        const isConfirmed =
            result?.isConfirmed === true ||
            result?.value === true;

        if (!isConfirmed) {
            return;
        }

        const deleteResult = await deleteHoliday(holiday.id);

        if (!deleteResult.ok) {
            await showCommonAlert({
                title: "Erro",
                message: deleteResult.error,
                fallbackMessage: "Não foi possível excluir o feriado.",
                icon: "error",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });

            return;
        }

        await showCommonAlert({
            title: "Sucesso",
            message: "Feriado excluído com sucesso.",
            icon: "success",
            customClass: {
                container: "common-alert__container",
                confirmButton: "alert-form-button"
            }
        });

        loadHolidays({
            page: holidayPagination.page,
            size: holidayPagination.size,
            showEmptyAlert: true
        });
    };

    const handleEditHoliday = async holiday => {
        const result = await fetchHolidayById(holiday.id);

        if (!result.ok) {
            await showCommonAlert({
                title: "Erro",
                message: result.error,
                fallbackMessage: "Não foi possível carregar o feriado.",
                icon: "error",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });

            return;
        }

        setHolidayValidationErrors({});
        setClearedValidationErrors({});
        setHolidayFormValues(result.holiday);
        holidayModal.openEdit(result.holiday);
    };

    const columns = holidayColumnsConfig({
        onEdit: handleEditHoliday,
        onDelete: handleDeleteHoliday
    });

    const holidayCounterLabel =
        holidayPagination.totalElements === 1
            ? "1 feriado"
            : `${holidayPagination.totalElements} feriados`;

    return (
        <>
            <PageLoading
                states={[
                    {
                        loading: holidaysLoading,
                        message: "Carregando feriados..."
                    },
                    {
                        loading: createHolidayLoading,
                        message: "Cadastrando feriado..."
                    },
                    {
                        loading: deleteHolidayLoading,
                        message: "Excluindo feriado..."
                    },
                    {
                        loading: holidayByIdLoading,
                        message: "Carregando feriado..."
                    },
                    {
                        loading: updateHolidayLoading,
                        message: "Salvando feriado..."
                    }
                ]}
            />

            <div className="holiday-management">
                <AdminSection
                    actionLabel="Incluir feriado"
                    actionStartIcon="plusCircle"
                    onAction={handleOpenCreate}
                >
                    <div className="holiday-management__section-header">
                        <h3 className="holiday-management__section-title">
                            Feriados cadastrados
                        </h3>

                        <span className="holiday-management__counter">
                            {holidayCounterLabel}
                        </span>
                    </div>

                    <TableGeneric
                        columns={columns}
                        data={holidays}
                        rowKey="id"
                    />

                    {holidayPagination.totalPages > 1 && (
                        <Pagination
                            pageCount={holidayPagination.totalPages}
                            currentPage={holidayPagination.page}
                            onPageChange={handlePageChange}
                        />
                    )}
                </AdminSection>

                <FormModal
                    open={holidayModal.isOpen}
                    title={holidayModal.isEditing ? "Editar feriado" : "Incluir feriado"}
                    formId="holiday-form"
                    formFields={formFields}
                    errors={visibleHolidayValidationErrors}
                    onClose={handleCloseHolidayModal}
                    onSubmit={handleSubmitHoliday}
                    onFieldChange={handleHolidayFieldChange}
                    submitLabel="Salvar"
                    cancelLabel="Cancelar"
                    submitDisabled={
                        createHolidayLoading ||
                        updateHolidayLoading ||
                        deleteHolidayLoading ||
                        holidayByIdLoading
                    }
                />
            </div>
        </>
    );
};

export default CadastroFeriados;